import { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import {
  applyForceRefresh,
  isInBannerDebounce,
  markBannerAcknowledged,
  rememberDismissedVersion,
} from "../lib/version";
import { useVersionWatcher } from "../hooks/useVersionWatcher";

/** Top-of-screen banner that appears when the running server reports a
 *  build version that differs from the one this bundle was built with.
 *  One tap on Refresh runs the SW-unregister + cache-wipe + reload
 *  routine, so the user picks up the new bundle without having to
 *  remember the old "hard-refresh on your phone" handoff.
 *
 *  Layer D of the noise-reduction work (W1.7): the auto-refresh
 *  countdown is now five minutes (was 30 s) and only kicks in when
 *  the tab has been idle the entire time. Five minutes matches a
 *  typical CDN-edge cache window and means the user is much less
 *  likely to have the bundle yanked out from under them mid-task.
 *  The countdown is paused while the tab is hidden so the auto-
 *  refresh never fires on a backgrounded phone.
 *
 *  Layer E of the reframe (W1.7): the copy reads as a soft offer
 *  ("Memso has a fresh build ready") rather than an urgent alert.
 *  The interrupt cost is the same; the framing matters because the
 *  banner only fires for kind:major commits, which the user opts
 *  into seeing — they should arrive feeling helpful, not nagging.
 *
 *  The dismiss X is a "remind me later" - it hides the banner for the
 *  remainder of the session but doesn't disable the version watcher,
 *  so a *further* deploy will pop the banner again. */
const IDLE_AUTO_REFRESH_MS = 5 * 60 * 1000;

export function UpdateBanner() {
  const { mismatch, serverKind, serverVersion, builtVersion } = useVersionWatcher();
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const refreshLockRef = useRef(false);

  // Layer C of the noise-reduction work (polish-26): the
  // popup-style banner only fires for kind:major releases. Minor +
  // patch mismatches still advance the WhatsNew sparkle-dot count
  // (rendered by the header's WhatsNewIcon), so the user sees them
  // when they're ready instead of being interrupted.
  const isMajor = serverKind === "major";
  // Layer B backstop: even on major, if the user has already
  // acknowledged a banner in the last 30 minutes, suppress the
  // popup. The dot count keeps advancing.
  const debounced = isInBannerDebounce();
  const visible = mismatch && isMajor && !debounced && !dismissed;

  // Idle auto-refresh: countdown only runs while the banner is visible
  // AND the page is in the foreground. Tapping anywhere on the page
  // resets the countdown - that's "the user is active, don't yank the
  // bundle out from under them mid-tap."
  useEffect(() => {
    if (!visible) {
      setSecondsLeft(null);
      return;
    }
    let remainingMs = IDLE_AUTO_REFRESH_MS;
    setSecondsLeft(Math.ceil(remainingMs / 1000));

    let lastTick = Date.now();
    const tick = () => {
      if (document.visibilityState !== "visible") {
        // Pause the countdown while backgrounded.
        lastTick = Date.now();
        return;
      }
      const now = Date.now();
      remainingMs -= now - lastTick;
      lastTick = now;
      if (remainingMs <= 0) {
        triggerRefresh();
        return;
      }
      setSecondsLeft(Math.ceil(remainingMs / 1000));
    };
    const interval = window.setInterval(tick, 1000);

    const resetCountdown = () => {
      remainingMs = IDLE_AUTO_REFRESH_MS;
      lastTick = Date.now();
      setSecondsLeft(Math.ceil(remainingMs / 1000));
    };
    // Activity signals we listen for as "user is here, don't auto-refresh".
    // Keep the set tight - we don't want every mouse-jitter to reset the
    // timer, but a real interaction (tap, key, scroll) should.
    const events: (keyof DocumentEventMap)[] = ["pointerdown", "keydown", "scroll"];
    for (const e of events) document.addEventListener(e, resetCountdown, { passive: true });

    return () => {
      window.clearInterval(interval);
      for (const e of events) document.removeEventListener(e, resetCountdown);
    };
    // triggerRefresh / refreshing / dismissed deliberately omitted -
    // we want the countdown to start fresh only when `visible` flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const triggerRefresh = async () => {
    if (refreshLockRef.current) return;
    refreshLockRef.current = true;
    setRefreshing(true);
    if (serverVersion) rememberDismissedVersion(serverVersion);
    markBannerAcknowledged();
    await applyForceRefresh();
    // applyForceRefresh navigates; if for some reason it doesn't, the
    // lock prevents a double-fire and the spinner stays up.
  };

  const dismissBanner = () => {
    if (serverVersion) rememberDismissedVersion(serverVersion);
    markBannerAcknowledged();
    setDismissed(true);
  };

  if (!visible) return null;

  const shortServer = (serverVersion || "unknown").slice(0, 32);
  const shortBuilt = (builtVersion || "unknown").slice(0, 32);

  function formatCountdown(s: number): string {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r === 0 ? `${m}m` : `${m}m ${r}s`;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-[55] mx-auto max-w-2xl px-3 pt-2 pointer-events-none"
    >
      <div className="pointer-events-auto rounded-xl border-2 border-sky-400 dark:border-sky-500/70 bg-sky-50 dark:bg-sky-900/70 backdrop-blur-md shadow-lg shadow-sky-300/30 dark:shadow-black/40 px-3 py-2.5 flex items-center gap-3 animate-modalIn motion-reduce:animate-none">
        <div className="w-9 h-9 rounded-full bg-sky-500/20 dark:bg-sky-500/30 flex items-center justify-center flex-shrink-0">
          <RefreshCw className={`w-4 h-4 text-sky-700 dark:text-sky-200 ${refreshing ? "animate-spin" : ""}`} strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-sky-900 dark:text-sky-100 truncate">
            Memso has a fresh build ready
          </div>
          <div
            className="text-[11px] text-sky-800 dark:text-sky-200/80 truncate"
            title={`server ${shortServer} / built ${shortBuilt}`}
          >
            {refreshing
              ? "Refreshing…"
              : secondsLeft !== null && secondsLeft > 0
                ? `Refresh whenever — auto in ${formatCountdown(secondsLeft)}`
                : "Refresh whenever — tap to load it now"}
          </div>
        </div>
        <button
          type="button"
          onClick={triggerRefresh}
          disabled={refreshing}
          className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 active:scale-[0.98] text-white text-sm font-semibold px-3 py-2 transition-colors disabled:opacity-60"
        >
          Refresh
        </button>
        <button
          type="button"
          onClick={dismissBanner}
          disabled={refreshing}
          aria-label="Dismiss for now"
          className="flex-shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center text-sky-700 dark:text-sky-200 hover:bg-sky-200/60 dark:hover:bg-sky-700/60 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
